import type { ShipTime } from '@felix/domain';
import { IconHistory } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import classes from './Forms.module.css';
import { fileKindOf, sortedFiles } from './record-model.js';
import { recordTime, shortHash } from './status.js';
import type { FormRecordDetail, FormRecordRevision } from './types.js';

type RevisionFile = FormRecordRevision['files'][number];

/**
 * Kayıt revizyonları ve onay izi (mimari belge §7.4; ADR 0042 §13, §16): her revizyon zamanı,
 * kişisi, gerekçesi, notu, içerik hash'i ve kendi dosyalarıyla; önceki revizyonların dosyaları
 * görünür kalır ve önizlenir. Onaylar karar, kişi, rol ve zamanla; bekleyen adım "NOT APPROVED –
 * awaiting <rol> since <tarih>" ve süre aşımıyla [MEPC.312 4.3.10]. İptal gerekçesiyle.
 */
export function RevisionList({
  record,
  timeZone,
  selected,
  onPreview,
}: {
  record: FormRecordDetail;
  /** Gemide geminin saat dilimi; ofiste `null` (UTC). */
  timeZone: ShipTime | null;
  selected: string | null;
  onPreview: (file: RevisionFile, revisionNo: number) => void;
}) {
  const { t } = useTranslation();
  const name = (id: string) => record.names[id] ?? '—';
  const role = (id: string) => record.roles?.[id] ?? '—';
  const revisions = [...record.revisions].sort((a, b) => b.revisionNo - a.revisionNo);
  return (
    <Panel
      title={t('forms.revisions.title')}
      icon={<IconHistory size={17} stroke={1.8} />}
      className={classes.revisionsPanel}
    >
      {record.void ? (
        <p className={classes.voidLine} role="note">
          {t('forms.revisions.void', {
            by: name(record.void.by),
            at: recordTime(record.void.at, timeZone),
            reason: record.void.reason,
          })}
        </p>
      ) : null}
      {record.pending ? (
        <p className={classes.awaiting} data-overdue={record.overdue || undefined}>
          {t('forms.revisions.awaiting', {
            role: (record.pending.approverRoleIds ?? [record.pending.approverRoleId])
              .map(role)
              .join(' / '),
            since: recordTime(record.pending.since, timeZone),
          })}
          {record.overdue ? (
            <StatusBadge tone="danger">{t('forms.revisions.overdue')}</StatusBadge>
          ) : null}
        </p>
      ) : null}
      {revisions.length === 0 ? (
        <p className={classes.muted}>{t('forms.revisions.none')}</p>
      ) : (
        <ol className={classes.revisionList}>
          {revisions.map((revision) => (
            <li key={revision.revisionNo} className={classes.revisionItem}>
              <div className={classes.revisionHead}>
                <strong>{t('forms.revisions.revision', { no: revision.revisionNo })}</strong>
                {revision.revisionNo > 1 ? (
                  <StatusBadge tone="info">{t('forms.revisions.amendment')}</StatusBadge>
                ) : (
                  <StatusBadge tone="neutral">{t('forms.revisions.first')}</StatusBadge>
                )}
                <span className={classes.muted}>
                  {recordTime(revision.at, timeZone)} · {name(revision.by)}
                </span>
                <span className={classes.hash} title={revision.contentHash}>
                  {t('forms.revisions.hash', { hash: shortHash(revision.contentHash) })}
                </span>
              </div>
              {revision.reason ? (
                <p className={classes.revisionReason}>
                  <span className={classes.label}>{t('forms.revisions.reason')}</span>{' '}
                  {revision.reason}
                </p>
              ) : null}
              {revision.note ? (
                <p className={classes.revisionNote}>
                  <span className={classes.label}>{t('forms.revisions.note')}</span> {revision.note}
                </p>
              ) : null}
              <ul className={classes.revisionFiles} aria-label={t('forms.files.title')}>
                {sortedFiles(revision.files).map((file) => (
                  <li key={`${revision.revisionNo}-${file.sha256}`}>
                    <button
                      type="button"
                      className={classes.revisionFile}
                      data-kind={fileKindOf(file)}
                      aria-current={selected === file.sha256 || undefined}
                      title={file.name}
                      onClick={() => onPreview(file, revision.revisionNo)}
                    >
                      {file.name}
                    </button>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}
      {record.approvals.length > 0 ? (
        <>
          <h3 className={classes.subheading}>{t('forms.revisions.approvals')}</h3>
          <ol className={classes.approvalList}>
            {record.approvals.map((approval) => (
              <li
                key={`${approval.step}-${approval.at}`}
                className={classes.approvalItem}
                data-decision={approval.decision}
              >
                <StatusBadge tone={approval.decision === 'approved' ? 'success' : 'danger'}>
                  {t(`forms.revisions.decisions.${approval.decision}`, { step: approval.step })}
                </StatusBadge>
                <span>
                  {name(approval.approverId)} · {role(approval.approverRoleId)}
                  {approval.office ? ` · ${t('forms.revisions.officeStep')}` : ''}
                </span>
                <span className={classes.muted}>
                  {recordTime(approval.at, timeZone)}
                  {timeZone ? ` · ${recordTime(approval.at, null)}` : ''}
                </span>
                {approval.contentHash ? (
                  // Onayın bağlandığı içerik (imza bloğu ile aynı kısa hash; ADR 0069)
                  <span className={classes.hash} title={approval.contentHash}>
                    {shortHash(approval.contentHash)}
                  </span>
                ) : null}
                {approval.approverRoleUnverified ? (
                  <StatusBadge tone="warning">{t('forms.revisions.roleUnverified')}</StatusBadge>
                ) : null}
                {approval.reason ? (
                  <p className={classes.revisionReason}>{approval.reason}</p>
                ) : null}
              </li>
            ))}
          </ol>
        </>
      ) : null}
    </Panel>
  );
}
